import React, { useRef, useEffect } from 'react';
import { motion, useAnimation } from 'framer-motion';

// Importando os SVGs
import AndroidIcon from './assets/images/tecnologies/android.svg';
import AwsIcon from './assets/images/tecnologies/aws.svg';
import AzureIcon from './assets/images/tecnologies/azure.svg';
import DockerIcon from './assets/images/tecnologies/docker.svg';
import ElasticIcon from './assets/images/tecnologies/elastic.svg';
import ExpressIcon from './assets/images/tecnologies/express.svg';
import FlutterIcon from './assets/images/tecnologies/flutter.svg';
import GcpIcon from './assets/images/tecnologies/gcp.svg';
import GraphqlIcon from './assets/images/tecnologies/graphql.svg';
import K8sIcon from './assets/images/tecnologies/k8s.svg';
import KotlinIcon from './assets/images/tecnologies/kotlin.svg';
import MeilisearchIcon from './assets/images/tecnologies/meilisearch.svg';
import MongodbIcon from './assets/images/tecnologies/mongodb.svg';
import MysqlIcon from './assets/images/tecnologies/mysql.svg';
import NodeIcon from './assets/images/tecnologies/node.svg';
import PhpIcon from './assets/images/tecnologies/php.svg';
import PythonIcon from './assets/images/tecnologies/python.svg';
import ReactIcon from './assets/images/tecnologies/react.svg';
import RedisIcon from './assets/images/tecnologies/redis.svg';
import RustIcon from './assets/images/tecnologies/rust.svg';
import SwiftIcon from './assets/images/tecnologies/swift.svg';
import TsIcon from './assets/images/tecnologies/ts.svg';
import VueIcon from './assets/images/tecnologies/vue.svg';

const StatBox = ({ number, description }) => (
  <motion.div 
    className="bg-gradient-to-br from-[#291B69] to-[#723AF1] rounded-[20px] p-4 w-[380px] h-[120px] flex flex-col items-start justify-center cursor-pointer transition-all duration-500 hover:shadow-[0_8px_30px_rgb(114,58,241,0.2)] hover:from-[#341F7D] hover:to-[#8348FF]"
    whileHover={{ 
      scale: 1.01,
      transition: { 
        duration: 0.4,
        ease: "easeOut"
      }
    }}
    initial={{ scale: 1 }}
    whileTap={{ scale: 0.99 }}
  >
    <span className="text-[#C6FF00] text-[48px] font-bold font-montserrat leading-none mb-2">{number}</span>
    <p className="text-white text-[22px] font-bold">{description}</p>
  </motion.div>
);

const TechCarousel = () => {
  const techLogos = [
    { src: AndroidIcon, alt: "Android" },
    { src: AwsIcon, alt: "AWS" },
    { src: AzureIcon, alt: "Azure" },
    { src: DockerIcon, alt: "Docker" },
    { src: ElasticIcon, alt: "Elasticsearch" },
    { src: ExpressIcon, alt: "Express.js" },
    { src: FlutterIcon, alt: "Flutter" },
    { src: GcpIcon, alt: "Google Cloud Platform" },
    { src: GraphqlIcon, alt: "GraphQL" },
    { src: K8sIcon, alt: "Kubernetes" },
    { src: KotlinIcon, alt: "Kotlin" },
    { src: MeilisearchIcon, alt: "Meilisearch" },
    { src: MongodbIcon, alt: "MongoDB" },
    { src: MysqlIcon, alt: "MySQL" },
    { src: NodeIcon, alt: "Node.js" },
    { src: PhpIcon, alt: "PHP" },
    { src: PythonIcon, alt: "Python" },
    { src: ReactIcon, alt: "React" },
    { src: RedisIcon, alt: "Redis" },
    { src: RustIcon, alt: "Rust" },
    { src: SwiftIcon, alt: "Swift" },
    { src: TsIcon, alt: "TypeScript" },
    { src: VueIcon, alt: "Vue.js" }
  ];

  // Duplicando a lista para o loop contínuo
  const duplicatedLogos = [...techLogos, ...techLogos];

  const controls = useAnimation();
  const carouselRef = useRef(null);
  
  useEffect(() => {
    const startAnimation = async () => {
      await controls.start({
        x: [0, -6000],
        transition: {
          x: {
            repeat: Infinity,
            repeatType: "loop",
            duration: 60,
            ease: "linear"
          }
        }
      });
    };
    
    startAnimation();
  }, [controls]);

  return (
    <div className="relative w-full h-[10rem] flex items-start overflow-hidden"> 
      <motion.div 
        ref={carouselRef}
        className="flex absolute h-full left-0 top-0" 
        animate={controls}
      >
        {duplicatedLogos.map((logo, index) => (
          <motion.div
            key={index}
            className="flex-shrink-0 h-full w-[5rem] mx-8"
            whileHover={{ scale: 1.1 }}
          >
            <img 
              src={logo.src} 
              alt={logo.alt}
              className="h-full w-full object-contain" 
            />
          </motion.div>
        ))}
      </motion.div>
    </div>
  );
};

const ModularArchitectureSection = ({ id }) => {
  const stats = [
    { number: "+25 anos", description: "de experiência somada" },
    { number: "+1.250.000", description: "linhas de código escritas" },
    { number: "100%", description: "dos clientes satisfeitos" }
  ];

  return (
    <section className="bg-white py-4 lg:my-12" id={id}>
      <div className="px-4 lg:w-4/5 mx-auto"> 
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-evenly"> 
          {/* Bloco de Texto Principal */}
          <div className="lg:w-1/2">
            <motion.div
              className="text-3xl font-bold"
              style={{ fontFamily: 'ZenDots' }} 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <span className="text-[#291B69] sm:text-4xl">Criamos tecnologia que impulsiona negócios e </span>
              <span className="bg-gradient-to-r from-purple-600 to-purple-400 bg-clip-text text-transparent sm:text-4xl">transforma desafios em inovação.</span>
            </motion.div>
            <motion.p
              className="text-[#291B69] mt-4 text-2xl sm:text-3xl font-semibold mb-4 pe-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              Com as melhores tecnologias que movem o futuro:
            </motion.p>
          </div>

          {/* Bloco de Indicadores */}
          <div className=" flex flex-col pl-8">
            <motion.div 
              className="flex flex-col gap-2"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              {stats.map((stat, index) => (
                <StatBox key={index} {...stat} />
              ))}
            </motion.div>
          </div>
        </div>

        {/* Carrossel de Tecnologias */}
        <TechCarousel />
      </div>
    </section>
  );
};

export default ModularArchitectureSection;
